import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Field, inputClass } from '../ds/Dialog';
import { Button } from '../ds';
import { ErrorNote } from './ErrorNote';
import { DAW_OPTIONS } from '../../utils/instruments';

/** Name a new project and pick the DAW, so plugin moves use that DAW's own plugins */
export const NewProjectDialog = ({ open, onClose, onCreate, defaultDaw }) => {
  const [name, setName] = useState('');
  const [daw, setDaw] = useState(DAW_OPTIONS[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const nameId = useId();
  const dawId = useId();

  useEffect(() => {
    if (!open) return;
    setName('');
    setDaw(DAW_OPTIONS.includes(defaultDaw) ? defaultDaw : DAW_OPTIONS[0]);
    setBusy(false);
    setError(null);
  }, [open, defaultDaw]);

  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate({ name: name.trim(), daw });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="New project" busy={busy}>
      <form onSubmit={submit} className="flex flex-col gap-space-6">
        <Field id={nameId} label="Project name">
          <input id={nameId} type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Late Night Drive" className={inputClass} />
        </Field>
        <Field id={dawId} label="Your DAW" hint="Plugin moves use its own plugins">
          <select id={dawId} value={daw} onChange={(event) => setDaw(event.target.value)} className={inputClass}>
            {DAW_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex flex-wrap justify-end gap-space-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create project'}</Button>
        </div>
      </form>
    </Dialog>
  );
};

/** Rename the project from its menu in the studio bar */
export const RenameProjectDialog = ({ open, onClose, project, onRename }) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const nameId = useId();

  useEffect(() => {
    if (!open) return;
    setName(project?.name || '');
    setBusy(false);
    setError(null);
  }, [open, project?.name]);

  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onRename(name.trim());
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Rename project" busy={busy}>
      <form onSubmit={submit} className="flex flex-col gap-space-6">
        <Field id={nameId} label="Project name">
          <input id={nameId} type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={inputClass} />
        </Field>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex flex-wrap justify-end gap-space-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy || !name.trim()}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      </form>
    </Dialog>
  );
};

/** Delete the project and all its clips, after saying what that means */
export const DeleteProjectDialog = ({ open, onClose, project, onDelete }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setError(null);
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  const clips = project?.clips?.length ?? 0;

  return (
    <Dialog open={open} onClose={onClose} title="Delete this project?" subtitle={project?.name} busy={busy}>
      <div className="flex flex-col gap-space-6">
        <p className="m-0 text-body text-ink-soft">
          {clips > 0
            ? `This deletes ${project.name} and its ${clips === 1 ? 'clip' : `${clips} clips`}, with every version and chat. This can't be undone.`
            : `This deletes ${project?.name}. This can't be undone.`}
        </p>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex flex-wrap justify-end gap-space-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Keep it</Button>
          <Button onClick={confirm} disabled={busy}>{busy ? 'Deleting…' : 'Delete project'}</Button>
        </div>
      </div>
    </Dialog>
  );
};

/** Shown when an unverified account tries to analyze something */
export const VerifyDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} title="Verify your email first">
    <div className="flex flex-col gap-space-6">
      <p className="m-0 text-body text-ink-soft">
        Analyses need a verified account. Check your inbox for the link we sent, or get a new one.
      </p>
      <div className="flex flex-wrap justify-end gap-space-3">
        <Button variant="secondary" onClick={onClose}>Not now</Button>
        <Button as={Link} to="/verify-pending">Verify my email</Button>
      </div>
    </div>
  </Dialog>
);
